import { CheckCircle2, Handshake } from 'lucide-react';
import * as React from 'react';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { CalendarArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { gradeVariant, PILL_CLASS } from '@/lib/badges';
import {
  useAnnualReviews,
  useFinaliseAnnualReview,
  type AnnualReviewRow,
} from '@/lib/annual-api';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

/**
 * The annual appraisal worklist - last 15 days of March.
 *
 * The suggested rating is shown next to the final one rather than pre-filled and
 * forgotten: a reviewer who only ever confirms a computed average is not reviewing,
 * and the whole point of the management stage is that a human can disagree with the
 * arithmetic and say why.
 */
export function FinalRatingsPage() {
  const { data, isPending, error } = useAnnualReviews();
  const [finalising, setFinalising] = React.useState<AnnualReviewRow | null>(null);

  const rows = data ?? [];
  const settled = rows.filter((r) => r.completedAt !== null).length;
  const blocked = rows.filter((r) => r.completedAt === null && !r.oneOnOneAcknowledged).length;
  const promotions = rows.filter((r) => r.promotionRecommended).length;

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Settled"
          value={isPending ? '—' : settled}
          caption={`${settled} of ${rows.length} employees`}
        />
        <StatTile
          label="Awaiting final rating"
          value={isPending ? '—' : rows.length - settled}
          caption="cleared the review chain, not yet signed off"
        />
        <StatTile
          label="Blocked on 1-on-1"
          value={isPending ? '—' : blocked}
          tone={blocked > 0 ? 'warning' : 'default'}
          caption={
            blocked === 0
              ? 'every discussion is acknowledged'
              : 'employee has not acknowledged the notes'
          }
        />
        <StatTile
          label="Promotion recommended"
          value={isPending ? '—' : promotions}
          caption={promotions === 0 ? 'none so far' : 'flagged during final review'}
        />
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Suggested</TableHead>
                  <TableHead>Final</TableHead>
                  <TableHead>1-on-1</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}

                {error && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-destructive">
                      {error.message}
                    </TableCell>
                  </TableRow>
                )}

                {!isPending && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState art={CalendarArt} title="No annual reviews yet" />
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.employeeId}>
                    <TableCell className="font-medium">{row.employeeName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.departmentName ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.suggestedRating ?? <span className="text-muted-foreground">—</span>}
                      {row.suggestedRating !== null && (
                        <span className="ml-1 text-xs text-muted-foreground">avg of quarters</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.finalRating === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {row.finalRating}
                          {row.finalGrade && (
                            <Badge variant={gradeVariant(row.finalGrade)} className={PILL_CLASS}>
                              {row.finalGrade}
                            </Badge>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.oneOnOneAcknowledged ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CheckCircle2 className="size-3.5" /> Acknowledged
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-warning">
                          <Handshake className="size-3.5" /> Not yet
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={row.completedAt ? 'soft-success' : 'info'}
                        className={PILL_CLASS}
                      >
                        {row.completedAt ? 'Settled' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button hint={"Record the final rating for this person"}
                        variant="ghost"
                        size="sm"
                        disabled={row.completedAt !== null}
                        onClick={() => setFinalising(row)}
                      >
                        Finalise
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <FinaliseDialog row={finalising} onOpenChange={(open) => !open && setFinalising(null)} />
    </PageContainer>
  );
}

function FinaliseDialog({
  row,
  onOpenChange,
}: {
  row: AnnualReviewRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [rating, setRating] = React.useState('');
  const [feedback, setFeedback] = React.useState('');
  const [promotion, setPromotion] = React.useState(false);

  const finalise = useFinaliseAnnualReview(row?.employeeId ?? '');

  // Seed from the suggestion each time a different employee is opened, so the
  // reviewer starts from the arithmetic and edits away from it deliberately.
  React.useEffect(() => {
    if (row) {
      setRating(row.suggestedRating === null ? '' : String(row.suggestedRating));
      setFeedback('');
      setPromotion(row.promotionRecommended);
    }
  }, [row]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!row) return;

    const value = Number(rating);
    if (!Number.isFinite(value) || value < 0 || value > 10) {
      toast.error('Final rating must be between 0 and 10');
      return;
    }

    try {
      await finalise.mutateAsync({
        finalRating: value,
        overallFeedback: feedback.trim() || null,
        promotionRecommended: promotion,
      });
      toast.success(`${row.employeeName}'s annual review is settled`);
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not settle the review', { description: err(e) });
    }
  }

  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Final rating — {row?.employeeName}</DialogTitle>
            <DialogDescription>
              This is the year&apos;s rating, not a quarter&apos;s. It is recorded against{' '}
              {row?.employeeName ?? 'the employee'} permanently.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {row && !row.oneOnOneAcknowledged && (
              <Alert>
                <AlertDescription className="text-warning">
                  This employee has not acknowledged their 1-on-1 notes. The discussion comes before
                  the rating, so this will be rejected until they do.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="finalRating">Final rating (0-10)</Label>
              <Input
                id="finalRating"
                type="number"
                min={0}
                max={10}
                step={0.5}
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                {row?.suggestedRating === null
                  ? 'No completed quarters to average.'
                  : `Quarterly average is ${row?.suggestedRating}. Override it if the year says otherwise.`}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="overallFeedback">Overall feedback</Label>
              <Textarea
                id="overallFeedback"
                rows={4}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What defined their year, and what comes next."
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="promotion"
                checked={promotion}
                onCheckedChange={(v) => setPromotion(v === true)}
              />
              <Label htmlFor="promotion" className="font-normal">
                Recommend for promotion
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Settle the final rating — it becomes the person's result for the cycle"} type="submit" disabled={finalise.isPending}>
              {finalise.isPending ? 'Saving…' : 'Settle final rating'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
